import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { CalendarDays, Search, RotateCw, Download, Upload, X } from "lucide-react";

const STATUS_CYCLE = ["present", "absent", "half_day", "leave", "holiday"];
const STATUS_META = {
    present: { letter: "·", cls: "text-ink/20 hover:text-ink/40", title: "Present" },
    absent: { letter: "A", cls: "bg-red-500 text-white", title: "Absent" },
    half_day: { letter: "½", cls: "bg-amber-500 text-white", title: "Half day" },
    leave: { letter: "L", cls: "bg-blue-500 text-white", title: "Leave" },
    holiday: { letter: "H", cls: "bg-green-500 text-white", title: "Holiday" },
};

function defaultPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function daysInMonth(period) {
    if (!period || period.length !== 7) return 30;
    const [y, m] = period.split("-").map(Number);
    return new Date(y, m, 0).getDate();
}

function dateStr(period, day) {
    return `${period}-${String(day).padStart(2, "0")}`;
}

function isWeekend(period, day) {
    if (!period) return false;
    const [y, m] = period.split("-").map(Number);
    const d = new Date(y, m - 1, day).getDay();
    return d === 0 || d === 6;
}

export default function Attendance() {
    const [period, setPeriod] = useState(defaultPeriod());
    const [employees, setEmployees] = useState([]);
    const [records, setRecords] = useState({});  // {employee_id: {date: {id, status}}}
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [companyFilter, setCompanyFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: emps }, { data: atts }] = await Promise.all([
                api.get("/employees"),
                api.get("/attendance", { params: { period } }),
            ]);
            setEmployees(emps);
            const map = {};
            for (const a of atts) {
                map[a.employee_id] = map[a.employee_id] || {};
                map[a.employee_id][a.date] = { id: a.id, status: a.status };
            }
            setRecords(map);
        } catch (err) {
            toast.error("Failed to load attendance");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

    const days = daysInMonth(period);
    const dayList = Array.from({ length: days }, (_, i) => i + 1);
    const companies = useMemo(
        () => ["all", ...Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort()],
        [employees],
    );
    const filteredEmployees = useMemo(() => {
        const q = search.trim().toLowerCase();
        return employees.filter((e) => {
            if (companyFilter !== "all" && e.department !== companyFilter) return false;
            return employeeMatches(e, q);
        });
    }, [employees, search, companyFilter]);

    const getStatus = (emp_id, day) => {
        const date = dateStr(period, day);
        return records[emp_id]?.[date]?.status || "present";
    };

    const cycleStatus = async (emp_id, day) => {
        const date = dateStr(period, day);
        const current = getStatus(emp_id, day);
        const idx = STATUS_CYCLE.indexOf(current);
        const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
        // optimistic update
        const oldRecords = records;
        setRecords((r) => {
            const copy = { ...r, [emp_id]: { ...(r[emp_id] || {}) } };
            if (next === "present") {
                delete copy[emp_id][date];
            } else {
                copy[emp_id][date] = { ...(copy[emp_id][date] || {}), status: next };
            }
            return copy;
        });
        try {
            await api.post("/attendance", { employee_id: emp_id, date, status: next });
        } catch (err) {
            setRecords(oldRecords);
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        }
    };

    const markAllPresentInMonth = async () => {
        if (!window.confirm("Reset all attendance exceptions for this month? Everyone will be marked as Present (default).")) return;
        try {
            // Bulk-clear: hit DELETE on each existing record for the period
            const dels = Object.values(records).flatMap((dates) => Object.values(dates));
            await Promise.all(dels.map((r) => api.delete(`/attendance/${r.id}`)));
            toast.success(`Cleared ${dels.length} records`);
            load();
        } catch (err) {
            toast.error("Reset failed");
        }
    };

    const periodLabel = useMemo(() => {
        if (!period) return "";
        const [y, m] = period.split("-").map(Number);
        return new Date(y, m - 1, 1).toLocaleString("en-NG", { month: "long", year: "numeric" });
    }, [period]);

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/attendance/import-template", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "attendance_import_template.xlsx";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Could not download template");
        }
    };

    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);

    const onImport = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/attendance/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(data);
            toast.success(`Imported ${data.written} record${data.written === 1 ? "" : "s"}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="attendance-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Workforce</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Attendance register
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Click a cell to cycle Present → Absent → Half day → Leave → Holiday.
                        Absences are deducted from payroll automatically using <span className="font-medium text-ink">gross ÷ 30 × days absent</span>.
                    </p>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="overline block mb-2">Month</label>
                        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="attendance-month" />
                    </div>
                    <button onClick={downloadTemplate} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="attendance-template-button">
                        <Download className="w-4 h-4" /> Template
                    </button>
                    <label className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5 cursor-pointer" data-testid="attendance-import-button">
                        <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import"}
                        <input type="file" accept=".xlsx" className="hidden" onChange={onImport} disabled={importing} data-testid="attendance-import-input" />
                    </label>
                    <button onClick={markAllPresentInMonth} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="attendance-reset-button">
                        <RotateCw className="w-4 h-4" /> Reset month
                    </button>
                </div>
            </div>

            <div className="mt-8 bg-white border border-ink/10 p-4 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none" />
                    <input
                        type="search"
                        placeholder="Search name, staff no, department, division, phone…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border border-ink/15 py-2 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                        data-testid="attendance-search"
                    />
                </div>
                <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="border border-ink/15 py-2 px-3 text-sm bg-transparent focus:border-klein focus:outline-none" data-testid="attendance-department-filter">
                    {companies.map((c) => (
                        <option key={c} value={c}>{c === "all" ? "All departments" : c}</option>
                    ))}
                </select>
                <div className="flex items-center gap-3 text-xs text-ink/60 ml-auto">
                    {Object.entries(STATUS_META).filter(([k]) => k !== "present").map(([k, m]) => (
                        <span key={k} className="inline-flex items-center gap-1">
                            <span className={`w-5 h-5 inline-flex items-center justify-center text-[10px] font-bold ${m.cls}`}>{m.letter}</span>
                            {m.title}
                        </span>
                    ))}
                </div>
            </div>

            {importResult && (
                <div className="mt-4 bg-white border border-ink/10 p-5 flex items-start justify-between gap-6" data-testid="attendance-import-result">
                    <div className="flex-1 min-w-0">
                        <div className="overline">Last import</div>
                        <div className="mt-2 flex items-center gap-6 text-sm">
                            <span className="text-green-700">✓ {importResult.written} written</span>
                            {importResult.skipped > 0 && <span className="text-amber-700">⚠ {importResult.skipped} skipped</span>}
                        </div>
                        {importResult.errors && importResult.errors.length > 0 && (
                            <details className="mt-3">
                                <summary className="text-xs text-ink/60 cursor-pointer">Show errors ({importResult.errors.length})</summary>
                                <ul className="mt-2 text-xs text-ink/70 space-y-1 max-h-40 overflow-y-auto">
                                    {importResult.errors.map((e, i) => (<li key={`err-${i}-${String(e).slice(0, 30)}`}>· {e}</li>))}
                                </ul>
                            </details>
                        )}
                    </div>
                    <button onClick={() => setImportResult(null)} className="p-1 text-ink/50 hover:text-ink" aria-label="Dismiss">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            <div className="mt-4 bg-white border border-ink/10 overflow-x-auto" data-testid="attendance-grid">
                <div className="text-sm font-medium px-4 py-3 border-b border-ink/10 bg-bone/40 sticky top-0">
                    {periodLabel} <span className="text-ink/50 font-normal">— {filteredEmployees.length} staff</span>
                </div>
                <table className="border-collapse text-xs">
                    <thead>
                        <tr className="border-b border-ink/10">
                            <th className="text-left px-3 py-2 sticky left-0 bg-white z-10 min-w-[220px] border-r border-ink/10">Employee</th>
                            {dayList.map((d) => (
                                <th key={d} className={`px-1 py-2 font-normal text-[10px] ${isWeekend(period, d) ? "text-ink/30" : "text-ink/60"}`}>{d}</th>
                            ))}
                            <th className="text-right px-3 py-2 font-normal text-[10px] text-ink/60 sticky right-0 bg-white border-l border-ink/10">Absent</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={days + 2} className="px-6 py-12 text-center text-ink/40">Loading…</td></tr>
                        ) : filteredEmployees.length === 0 ? (
                            <tr><td colSpan={days + 2} className="px-6 py-12 text-center text-ink/40">No employees match.</td></tr>
                        ) : filteredEmployees.map((emp) => {
                            let absentCount = 0;
                            for (const d of dayList) {
                                const s = getStatus(emp.id, d);
                                if (s === "absent") absentCount += 1;
                                else if (s === "half_day") absentCount += 0.5;
                            }
                            return (
                                <tr key={emp.id} className="border-b border-ink/5 hover:bg-bone/30">
                                    <td className="px-3 py-1.5 sticky left-0 bg-white z-10 border-r border-ink/10" data-testid={`attendance-row-${emp.id}`}>
                                        <div className="text-ink truncate max-w-[200px]">{emp.first_name} {emp.last_name}</div>
                                        <div className="text-[10px] text-ink/50">{emp.employee_no || "—"}{emp.department ? ` · ${emp.department}` : ""}</div>
                                    </td>
                                    {dayList.map((d) => {
                                        const status = getStatus(emp.id, d);
                                        const meta = STATUS_META[status] || STATUS_META.present;
                                        const weekend = isWeekend(period, d);
                                        return (
                                            <td key={d} className="p-0 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => cycleStatus(emp.id, d)}
                                                    className={`w-7 h-7 flex items-center justify-center text-[10px] font-bold ${
                                                        status === "present"
                                                            ? `${weekend ? "text-ink/15" : meta.cls}`
                                                            : meta.cls
                                                    } hover:scale-110 transition-transform`}
                                                    data-testid={`att-cell-${emp.id}-${d}`}
                                                    title={`${dateStr(period, d)} — ${meta.title}`}
                                                >
                                                    {meta.letter}
                                                </button>
                                            </td>
                                        );
                                    })}
                                    <td className={`text-right px-3 py-1.5 sticky right-0 bg-white border-l border-ink/10 tabular-nums ${absentCount > 0 ? "text-red-700 font-medium" : "text-ink/30"}`}>
                                        {absentCount > 0 ? absentCount : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
